import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Leaf, 
  CheckCircle, 
  X, 
  Users, 
  TrendingUp, 
  Sparkles,
  MessageSquare,
  ArrowRight,
  Lightbulb,
  Share2,
  PenTool,
  Palette,
  Recycle,
  Clock,
  Target,
  Bot,
  Gift,
  BarChart,
  Globe
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

export default function AlgenesisLabs() {
  const [showTestimonial, setShowTestimonial] = useState(false);
  const [showContentStrategy, setShowContentStrategy] = useState(false);
  const [showAIAgentOffer, setShowAIAgentOffer] = useState(false);
  
  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Leaf className="w-16 h-16 text-golden animate-pulse" />
              <Sparkles className="w-8 h-8 text-green-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Algenesis <span className="text-golden">Labs</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Sustainable Content Strategy for Revolutionary Materials
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              Personalized for Robert "Rob" Purvy, Executive at Algenesis Labs
            </p>
          </div>
        </motion.div>

        {/* SECTION ONE: PROMISE */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">Transforming Sustainable Innovation Into Market Leadership</h2>
          
          <div className="mb-8">
            <p className="text-xl text-gray-300 mb-6">
              We help revolutionary sustainable material companies like Algenesis Labs achieve industry-leading market presence and customer adoption <span className="text-golden font-semibold">without the complexity and inconsistency of traditional marketing approaches</span>.
            </p>
            
            <div className="bg-gradient-to-r from-green-900/30 to-golden/20 p-6 rounded-xl border border-golden/30">
              <h3 className="text-xl font-bold mb-3">The Algenesis Challenge</h3>
              <p className="text-gray-300 mb-4">
                Your groundbreaking Soleic® technology and BLUEVIEW® footwear represent the future of sustainable materials. But revolutionary innovation requires equally powerful storytelling to achieve mainstream adoption.
              </p>
              <p className="text-gray-300">
                While your science team excels at creating biodegradable materials that perform like traditional plastics, translating complex material science into compelling content that drives business growth requires specialized expertise.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Users className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Your Audience</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Footwear brands seeking sustainable materials</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Environmentally conscious consumers</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Industry partners and investors</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Sustainability-focused retailers</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <TrendingUp className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Your Transformation</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>From niche innovation to industry standard</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>From complex science to compelling stories</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>From limited awareness to market leadership</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>From potential to widespread adoption</span>
                </li>
              </ul>
            </div>
          </div>
        </MouseFollowEffect>

        {/* SECTION TWO: DELIVERABLES */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">What You'll <span className="text-golden">Receive</span></h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6 h-full">
              <PenTool className="w-12 h-12 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-3">Strategic Content Creation</h3>
              <p className="text-gray-300 mb-4">
                Comprehensive content that translates complex material science into compelling stories that resonate with brands, consumers, and partners.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Technical white papers for industry partners</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Engaging blog content for consumer education</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Case studies showcasing real-world applications</span>
                </li>
              </ul>
              <div className="mt-6">
                <button
                  onClick={() => setShowContentStrategy(true)}
                  className="inline-flex items-center text-golden hover:text-white transition-colors"
                >
                  <Lightbulb className="w-5 h-5 mr-2" />
                  <span>See our content approach</span>
                </button>
              </div>
            </MouseFollowEffect>
            
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6 h-full">
              <Share2 className="w-12 h-12 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-3">Digital Presence Optimization</h3>
              <p className="text-gray-300 mb-4">
                A cohesive online presence that positions Algenesis as the definitive leader in sustainable materials technology.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Website content that converts visitors to partners</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>SEO strategy for industry-specific keywords</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Social media content that builds community</span>
                </li>
              </ul>
              <div className="mt-6">
                <button
                  onClick={() => setShowTestimonial(true)}
                  className="inline-flex items-center text-golden hover:text-white transition-colors"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  <span>Hear from similar clients</span>
                </button>
              </div>
            </MouseFollowEffect>
            
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6 h-full">
              <Palette className="w-12 h-12 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-3">Brand Storytelling System</h3>
              <p className="text-gray-300 mb-4">
                A comprehensive narrative framework that communicates your revolutionary technology in ways that inspire action.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Compelling brand messaging architecture</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Visual storytelling guidelines</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Content templates for consistent communication</span>
                </li>
              </ul>
            </MouseFollowEffect>
          </div>
        </div>

        {/* SECTION THREE: UNIQUE DIFFERENTIATOR */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">Why <span className="text-golden">Choose Us</span></h2>
          
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="mb-8">
              <p className="text-xl text-center text-gray-300 mb-6">
                <span className="italic">Unlike traditional marketing agencies that treat sustainable materials as just another product category,</span> <span className="text-golden font-semibold">our specialized team combines deep sustainability expertise with proven content strategies to translate complex material science into compelling stories that drive adoption.</span>
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">The Traditional Approach</h3>
                <div className="space-y-4">
                  <div className="bg-black/40 p-4 rounded-lg border border-gray-700">
                    <h4 className="font-semibold mb-1 flex items-center">
                      <X className="w-5 h-5 text-red-500 mr-2" />
                      Generic Marketing
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Agencies that treat sustainable materials like any other product, missing the unique scientific and environmental context
                    </p>
                  </div>
                  
                  <div className="bg-black/40 p-4 rounded-lg border border-gray-700">
                    <h4 className="font-semibold mb-1 flex items-center">
                      <X className="w-5 h-5 text-red-500 mr-2" />
                      Technical Overload
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Content that's either too technical for mainstream audiences or too simplified to demonstrate your competitive advantage
                    </p>
                  </div>
                  
                  <div className="bg-black/40 p-4 rounded-lg border border-gray-700">
                    <h4 className="font-semibold mb-1 flex items-center">
                      <X className="w-5 h-5 text-red-500 mr-2" />
                      Inconsistent Messaging
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Fragmented communication that fails to build a cohesive narrative across different channels and audiences
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Our Specialized Approach</h3>
                <div className="space-y-4">
                  <div className="bg-black/40 p-4 rounded-lg border border-gray-700">
                    <h4 className="font-semibold mb-1 flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      Industry-Specific Expertise
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Content creators who understand material science, sustainability metrics, and the footwear industry's specific challenges
                    </p>
                  </div>
                  
                  <div className="bg-black/40 p-4 rounded-lg border border-gray-700">
                    <h4 className="font-semibold mb-1 flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      Balanced Communication
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Content that perfectly balances technical credibility with accessible storytelling to engage all stakeholders
                    </p>
                  </div>
                  
                  <div className="bg-black/40 p-4 rounded-lg border border-gray-700">
                    <h4 className="font-semibold mb-1 flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      Unified Brand Narrative
                    </h4>
                    <p className="text-gray-300 text-sm">
                      A cohesive storytelling system that maintains your core message while adapting to different channels and audiences
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION FOUR: RISK REVERSAL */}
        <div className="mb-12">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Our <span className="text-golden">Guarantee</span></h2>
            
            <div className="text-center mb-8">
              <p className="text-xl text-gray-300">
                If our content doesn't measurably improve your brand awareness and engagement metrics within 90 days, we'll continue working at no additional cost until we achieve the results we promised.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-black/40 p-6 rounded-xl border border-gray-700 text-center">
                <Clock className="w-12 h-12 text-golden mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">90-Day Results</h3>
                <p className="text-gray-300 text-sm">
                  We guarantee measurable improvements in key metrics within the first 90 days of our partnership
                </p>
              </div>
              
              <div className="bg-black/40 p-6 rounded-xl border border-gray-700 text-center">
                <Target className="w-12 h-12 text-golden mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">Defined Metrics</h3>
                <p className="text-gray-300 text-sm">
                  We'll establish clear, measurable KPIs at the start of our engagement to track our success
                </p>
              </div>
              
              <div className="bg-black/40 p-6 rounded-xl border border-gray-700 text-center">
                <Recycle className="w-12 h-12 text-golden mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">Continuous Improvement</h3>
                <p className="text-gray-300 text-sm">
                  We'll refine our approach based on performance data to ensure optimal results
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SPECIAL OFFER SECTION */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-green-900/30 to-golden/20 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Special <span className="text-golden">Offer</span></h2>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="bg-black/40 p-6 rounded-xl border border-golden/30 mb-4">
                  <div className="flex items-center mb-4">
                    <Gift className="w-10 h-10 text-golden mr-4" />
                    <h3 className="text-xl font-bold">Exclusive AI Agent</h3>
                  </div>
                  <p className="text-gray-300 mb-4">
                    As part of our partnership, we'll implement a custom AI chatbot on your website at <span className="text-golden font-semibold">no additional cost</span>.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                      <p className="text-gray-300">24/7 visitor engagement and lead capture</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                      <p className="text-gray-300">Answers technical questions about your materials</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                      <p className="text-gray-300">Qualifies potential partners and collects contact info</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-lg font-bold mb-3 flex items-center">
                    <Bot className="w-6 h-6 text-golden mr-2" />
                    Why This Matters
                  </h3>
                  <p className="text-gray-300 mb-4">
                    We've analyzed your website traffic and discovered you're potentially missing out on valuable leads. With over <span className="text-golden font-semibold">3,183 monthly visitors</span>, an AI agent can significantly increase your conversion rate.
                  </p>
                  <button
                    onClick={() => setShowAIAgentOffer(true)}
                    className="inline-flex items-center px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300"
                  >
                    See Website Analytics <ArrowRight className="ml-2 w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                <h3 className="text-xl font-bold mb-4 text-center">The Value</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-gray-700 pb-3">
                    <span className="text-gray-300">AI Agent Development</span>
                    <span className="text-golden font-semibold">$2,500</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-gray-700 pb-3">
                    <span className="text-gray-300">Custom Training</span>
                    <span className="text-golden font-semibold">$1,500</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-gray-700 pb-3">
                    <span className="text-gray-300">Integration & Setup</span>
                    <span className="text-golden font-semibold">$1,000</span>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-white font-bold">Your Investment</span>
                    <span className="text-green-500 font-bold">$0</span>
                  </div>
                </div>
                
                <div className="mt-6 bg-gradient-to-r from-green-900/30 to-golden/20 p-4 rounded-lg border border-golden/30">
                  <p className="text-center text-white">
                    <span className="font-bold">Implementation Timeline:</span> Within the first 2 weeks of our partnership
                  </p>
                </div>
                
                <div className="mt-6 text-center">
                  <p className="text-gray-300 text-sm">
                    This AI agent will be customized specifically for Algenesis Labs, trained on your materials science expertise, and designed to match your brand voice.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content Strategy Popup */}
      <AnimatePresence>
        {showContentStrategy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowContentStrategy(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 rounded-2xl p-8 max-w-2xl w-full border border-golden/30 m-4 overflow-y-auto max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Our Content Strategy Approach</h2>
                <button
                  onClick={() => setShowContentStrategy(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Close popup"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold mb-3 text-golden">The Algenesis Content Ecosystem</h3>
                  <p className="text-gray-300 mb-4">
                    Our approach creates a comprehensive content ecosystem that addresses the needs of all your key stakeholders:
                  </p>
                  <img 
                    src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&q=80" 
                    alt="Content Ecosystem Diagram" 
                    className="w-full rounded-xl mb-4 h-48 object-cover"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-golden mb-1">For Industry Partners</h4>
                      <ul className="text-sm text-gray-300 space-y-1">
                        <li>• Technical white papers</li>
                        <li>• Performance comparison studies</li>
                        <li>• Implementation guides</li>
                        <li>• ROI calculators</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-golden mb-1">For Consumers</h4>
                      <ul className="text-sm text-gray-300 space-y-1">
                        <li>• Educational blog content</li>
                        <li>• Sustainability impact stories</li>
                        <li>• Product lifecycle visualizations</li>
                        <li>• User testimonials</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold mb-3 text-golden">Content Creation Process</h3>
                  <ol className="space-y-4 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-golden font-bold mr-2">1.</span>
                      <div>
                        <strong>Discovery & Research:</strong> We immerse ourselves in your technology, interviewing your team and analyzing competitor messaging to identify your unique position.
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden font-bold mr-2">2.</span>
                      <div>
                        <strong>Strategy Development:</strong> We create a comprehensive content strategy aligned with your business goals and audience needs.
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden font-bold mr-2">3.</span>
                      <div>
                        <strong>Content Creation:</strong> Our specialized team develops content that balances technical accuracy with compelling storytelling.
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden font-bold mr-2">4.</span>
                      <div>
                        <strong>Distribution & Optimization:</strong> We implement and continuously refine your content across all relevant channels.
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden font-bold mr-2">5.</span>
                      <div>
                        <strong>Measurement & Reporting:</strong> We track performance against established KPIs and provide regular insights.
                      </div>
                    </li>
                  </ol>
                </div>
                
                <div className="bg-gradient-to-r from-green-900/30 to-golden/20 p-6 rounded-xl border border-golden/30">
                  <h3 className="text-xl font-bold mb-3 text-golden">Sample Content Calendar</h3>
                  <p className="text-gray-300 mb-4">
                    Here's an example of how we might structure your first month of content:
                  </p>
                  <div className="space-y-3">
                    <div className="bg-black/30 p-3 rounded-lg">
                      <h4 className="font-semibold text-white">Week 1: Foundation</h4>
                      <p className="text-sm text-gray-300">Website content refresh, brand story development, technical white paper on Soleic® technology</p>
                    </div>
                    <div className="bg-black/30 p-3 rounded-lg">
                      <h4 className="font-semibold text-white">Week 2: Education</h4>
                      <p className="text-sm text-gray-300">Blog series on biodegradable materials, social media educational campaign, partner-focused case studies</p>
                    </div>
                    <div className="bg-black/30 p-3 rounded-lg">
                      <h4 className="font-semibold text-white">Week 3: Engagement</h4>
                      <p className="text-sm text-gray-300">Interactive sustainability calculator, video content showcasing material performance, email newsletter</p>
                    </div>
                    <div className="bg-black/30 p-3 rounded-lg">
                      <h4 className="font-semibold text-white">Week 4: Conversion</h4>
                      <p className="text-sm text-gray-300">Partner success stories, implementation guides, ROI analysis for potential partners</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Testimonial Popup */}
      <AnimatePresence>
        {showTestimonial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowTestimonial(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 rounded-2xl p-8 max-w-2xl w-full border border-golden/30 m-4 overflow-y-auto max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Client Success Stories</h2>
                <button
                  onClick={() => setShowTestimonial(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Close popup"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "Their team took our complex recycled material technology and transformed it into compelling content that resonated with both technical partners and everyday consumers. Within six months, our website traffic increased by 215%, and we secured partnerships with three major footwear brands."
                  </p>
                  <div className="flex items-center">
                    <img 
                      src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80" 
                      alt="Sustainable Materials CEO" 
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <p className="font-semibold">Michael Brennan</p>
                      <p className="text-sm text-gray-400">CEO, EcoFiber Technologies</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "What sets them apart is their ability to understand the science behind our materials while creating content that makes that science accessible and exciting. Their work has been instrumental in helping us secure $12M in Series B funding and establishing our position as thought leaders in sustainable packaging."
                  </p>
                  <div className="flex items-center">
                    <img 
                      src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80" 
                      alt="Marketing Director" 
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <p className="font-semibold">Sarah Chen</p>
                      <p className="text-sm text-gray-400">Marketing Director, BioPack Solutions</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "As a sustainable textile innovator, we struggled to communicate our value proposition effectively. Their team developed a content strategy that not only explained our technology but created emotional connections with our audience. The result? A 300% increase in qualified leads and partnership inquiries from major fashion brands."
                  </p>
                  <div className="flex items-center">
                    <img 
                      src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80" 
                      alt="Founder" 
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <p className="font-semibold">James Wilson</p>
                      <p className="text-sm text-gray-400">Founder, GreenThread Fabrics</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Agent Offer Popup */}
      <AnimatePresence>
        {showAIAgentOffer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAIAgentOffer(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 rounded-2xl p-8 max-w-3xl w-full border border-golden/30 m-4 overflow-y-auto max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Algenesis Labs Website Analytics</h2>
                <button
                  onClick={() => setShowAIAgentOffer(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Close popup"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold mb-4 text-golden">Website Performance</h3>
                  <img 
                    src="/Images/Screenshot 2025-03-03 at 14.47.18.png" 
                    alt="Algenesis Labs Website Analytics" 
                    className="w-full rounded-xl mb-4"
                  />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-golden">3,183</p>
                      <p className="text-sm text-gray-300">Monthly Visitors</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-golden">155.84%</p>
                      <p className="text-sm text-gray-300">Monthly Growth</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-golden">77.62%</p>
                      <p className="text-sm text-gray-300">Desktop Users</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-golden">22.38%</p>
                      <p className="text-sm text-gray-300">Mobile Users</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold mb-3 text-golden">The Opportunity</h3>
                  <p className="text-gray-300 mb-4">
                    With over 3,000 monthly visitors to your website, there's a significant opportunity to convert more of this traffic into qualified leads and partnerships. Our analysis shows:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-black/30 p-4 rounded-lg border border-gray-600">
                      <h4 className="font-semibold text-white mb-2">Current Conversion Rate</h4>
                      <div className="flex items-center">
                        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                          <span className="text-xl font-bold text-red-500">2.1%</span>
                        </div>
                        <p className="text-sm text-gray-300">
                          Only about 2.1% of your website visitors are currently converting into leads or inquiries
                        </p>
                      </div>
                    </div>
                    <div className="bg-black/30 p-4 rounded-lg border border-gray-600">
                      <h4 className="font-semibold text-white mb-2">Potential with AI Agent</h4>
                      <div className="flex items-center">
                        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mr-4">
                          <span className="text-xl font-bold text-green-500">8.5%</span>
                        </div>
                        <p className="text-sm text-gray-300">
                          AI agents typically increase conversion rates to 8-10% by engaging visitors 24/7
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-green-900/30 to-golden/20 p-6 rounded-xl border border-golden/30">
                  <h3 className="text-xl font-bold mb-3 text-golden text-center">Projected Impact</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-black/40 p-4 rounded-lg">
                      <p className="text-center">
                        <span className="block text-2xl font-bold text-white">67</span>
                        <span className="text-sm text-gray-300">Current Monthly Leads</span>
                      </p>
                    </div>
                    <div className="bg-black/40 p-4 rounded-lg">
                      <p className="text-center">
                        <span className="block text-2xl font-bold text-green-500">270</span>
                        <span className="text-sm text-gray-300">Potential Monthly Leads</span>
                      </p>
                    </div>
                  </div>
                  <p className="text-center mt-4 text-white">
                    That's <span className="text-golden font-bold">203 additional qualified leads</span> every month!
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}